// Sprite pixel analysis for the Workshop: the silhouette OUTLINE and
// pixel-perfect PICKING both derive from one read of an image's alpha channel,
// so a single offscreen-canvas pass (the expensive part) feeds both.
//
//  - loops: the opaque/transparent boundary as closed polylines in IMAGE-PIXEL
//    space (lattice coords 0..w / 0..h). The Workshop maps these through its
//    world→screen transform and strokes them at a constant screen-space width,
//    so the outline hugs the art at any zoom while its thickness never changes.
//    (Baking the outline into a scaled bitmap would let it shrink below a
//    screen pixel and alias away when zoomed out.)
//  - mask: a 1-bit-per-pixel opacity bitset used to hit-test clicks against the
//    actual art instead of the bounding box.
//
// The trace runs on the integer pixel grid, so loops are the faithful
// stair-stepped silhouette of pixel art; collinear runs are merged so a long
// straight edge is two points, not hundreds.

export interface SpriteAnalysis {
    natural: {w: number; h: number};
    // Each loop is an open list of {x, y} lattice points; the consumer closes
    // it (last point connects back to the first).
    loops: {x: number; y: number}[][];
    // Row-major opacity bitset, bit (y*w + x) set when that pixel is hittable.
    mask: Uint8Array;
}

// Outline edge: pixels at/above this alpha are "opaque". Soft anti-aliased halo
// pixels below it are treated as empty, so the outline hugs the visible shape.
export const DEFAULT_ALPHA_THRESHOLD = 128;

// Picking is more forgiving than the outline: any barely-visible pixel is
// clickable, so thin/feathered edges still grab.
export const DEFAULT_PICK_THRESHOLD = 16;

// Read an image's pixels via an offscreen canvas. Returns undefined if the
// canvas is tainted (cross-origin without CORS) or the image is empty — the
// caller then falls back to a plain bounding box.
function readImageData(image: HTMLImageElement): ImageData | undefined {
    const w = image.naturalWidth, h = image.naturalHeight;
    if (w <= 0 || h <= 0) return undefined;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    if (!ctx) return undefined;
    ctx.drawImage(image, 0, 0);
    try {
        return ctx.getImageData(0, 0, w, h);
    } catch {
        return undefined;   // tainted canvas
    }
}

// Analyse an already-loaded image: outline contours + pick mask, from one pixel
// pass. Returns undefined when the pixels can't be read or nothing is opaque.
export function analyzeSprite(
    image: HTMLImageElement,
    outlineThreshold: number = DEFAULT_ALPHA_THRESHOLD,
    pickThreshold: number = DEFAULT_PICK_THRESHOLD,
): SpriteAnalysis | undefined {
    const data = readImageData(image);
    if (!data) return undefined;
    const w = data.width, h = data.height;
    const alpha = data.data;

    // Pick mask (1 bit/pixel) — built in the same pass.
    const mask = new Uint8Array(Math.ceil((w * h) / 8));
    let opaqueCount = 0;
    for (let i = 0; i < w * h; i++) {
        if (alpha[i * 4 + 3] >= pickThreshold) {
            mask[i >> 3] |= 1 << (i & 7);
            opaqueCount++;
        }
    }
    if (opaqueCount === 0) return undefined;

    return {natural: {w, h}, loops: traceContours(alpha, w, h, outlineThreshold), mask};
}

export function maskAt(a: SpriteAnalysis, px: number, py: number): boolean {
    if (px < 0 || py < 0 || px >= a.natural.w || py >= a.natural.h) return false;
    const i = py * a.natural.w + px;
    return (a.mask[i >> 3] & (1 << (i & 7))) !== 0;
}

type Dir = readonly [number, number];

// Trace the opaque/transparent boundary into closed loops.
function traceContours(
    alpha: Uint8ClampedArray, w: number, h: number, threshold: number,
): {x: number; y: number}[][] {
    const opaque = (x: number, y: number): boolean =>
        x >= 0 && y >= 0 && x < w && y < h && alpha[(y * w + x) * 4 + 3] >= threshold;

    // Directed boundary half-edges around each opaque cell, oriented so the
    // filled cell sits on the RIGHT of the travel direction (y-down screen
    // space). Following them yields consistently-wound closed loops, and the
    // right-hand convention lets us resolve diagonal pinch points without the
    // loops crossing. Each edge is keyed start→end on the integer lattice.
    const key = (x: number, y: number) => y * (w + 1) + x;
    // For each start vertex, the list of reachable end vertices (as [ex, ey]).
    const out = new Map<number, [number, number][]>();
    const addEdge = (sx: number, sy: number, ex: number, ey: number) => {
        const k = key(sx, sy);
        const list = out.get(k);
        if (list) list.push([ex, ey]);
        else out.set(k, [[ex, ey]]);
    };

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            if (!opaque(x, y)) continue;
            if (!opaque(x, y - 1)) addEdge(x, y, x + 1, y);             // top
            if (!opaque(x + 1, y)) addEdge(x + 1, y, x + 1, y + 1);     // right
            if (!opaque(x, y + 1)) addEdge(x + 1, y + 1, x, y + 1);     // bottom
            if (!opaque(x - 1, y)) addEdge(x, y + 1, x, y);             // left
        }
    }
    if (out.size === 0) return [];

    // Walk the directed edges into closed loops. At a junction we prefer to
    // turn left, then go straight, then turn right — the tightest turn that
    // keeps a filled-on-right loop from self-crossing at diagonal touches.
    const turnPriority = (din: Dir, dcand: Dir): number => {
        // left, straight, right, reverse  ->  0, 1, 2, 3
        const left: Dir = [din[1], -din[0]];
        if (dcand[0] === left[0] && dcand[1] === left[1]) return 0;
        if (dcand[0] === din[0] && dcand[1] === din[1]) return 1;
        if (dcand[0] === -left[0] && dcand[1] === -left[1]) return 2;
        return 3;
    };

    const loops: {x: number; y: number}[][] = [];
    out.forEach((startList, startKey) => {
        while (startList.length > 0) {
            const sx = startKey % (w + 1);
            const sy = (startKey - sx) / (w + 1);
            const first = startList.pop()!;
            const loop: {x: number; y: number}[] = [{x: sx, y: sy}];
            let cx = first[0], cy = first[1];
            let din: Dir = [first[0] - sx, first[1] - sy];

            // Follow until we return to the loop's start vertex.
            let guard = (w + 1) * (h + 1) * 4 + 8;   // safety against bad data
            while (!(cx === sx && cy === sy) && guard-- > 0) {
                loop.push({x: cx, y: cy});
                const list = out.get(key(cx, cy));
                if (!list || list.length === 0) break;
                // Pick the best-turning outgoing edge.
                let bestIdx = 0, bestRank = 4;
                for (let i = 0; i < list.length; i++) {
                    const d: Dir = [list[i][0] - cx, list[i][1] - cy];
                    const rank = turnPriority(din, d);
                    if (rank < bestRank) { bestRank = rank; bestIdx = i; }
                }
                const next = list.splice(bestIdx, 1)[0];
                din = [next[0] - cx, next[1] - cy];
                cx = next[0]; cy = next[1];
            }
            if (loop.length >= 3) loops.push(mergeCollinear(loop));
        }
    });
    return loops;
}

// Drop interior points of straight runs: a point whose incoming and outgoing
// directions match adds nothing to an axis-aligned outline.
function mergeCollinear(loop: {x: number; y: number}[]): {x: number; y: number}[] {
    const n = loop.length;
    const out: {x: number; y: number}[] = [];
    for (let i = 0; i < n; i++) {
        const prev = loop[(i - 1 + n) % n];
        const cur = loop[i];
        const next = loop[(i + 1) % n];
        const dx1 = cur.x - prev.x, dy1 = cur.y - prev.y;
        const dx2 = next.x - cur.x, dy2 = next.y - cur.y;
        // Collinear if the cross product is zero and they point the same way.
        if (dx1 * dy2 - dy1 * dx2 === 0 && dx1 * dx2 + dy1 * dy2 > 0) continue;
        out.push(cur);
    }
    return out;
}
